import { ConfigMarket, IConfigMarket, IConfigState, IKline } from '@Redux/interfaces'
import { RootState } from '@Redux/store'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { fetchConfig } from '@App/api/config'
import { generateRequestKey } from '@App/sockets/helpers'

import { setConfigFrontServiceConnection } from '@Redux/slices/sockets/FrontServiceConnectionSlice'
import { setConfigProxyServiceConnection } from '@Redux/slices/sockets/ProxyServiceConnectionSlice'
import { setConfigUserServiceConnection } from '@Redux/slices/sockets/UserServiceConnectionSlice'

import {
  addFavoriteTitle,
  removeAllFavoriteTitles,
  removeFavoriteTitle,
  syncFavoritesFromLocalStorage,
} from '@UI/crypto_exchange/desktop/pages/charts/utils'

import { createNotificationObject } from '../notifications/NotificationSlice'
import {
  setConfigPublicServiceConnection,
  setMarketDataAsync,
  setMarketsByIdsDataAsync,
} from '../sockets/PublicServiceConnectionSlice'

import type { PayloadAction } from '@reduxjs/toolkit'

export const getMarket = (source_currency: string, target_currency: string, separator: string = '') =>
  [source_currency, target_currency].join(separator)

const initialState: IConfigState = {
  userServiceWsApi: '',
  proxyServiceWsApi: '',
  frontServiceWsApi: '',
  publicServiceWsApi: '',
  coinRateApi: '',
  currencies: [],
  system_currencies: [],
  shared_countries: [],
  markets: [],
  favoriteMarkets: [],
  activeFavoriteMarket: null,
  history_feeds: [],
  languages: [],
  isConfigLoading: false,
  is_freeze_reality_check: null,
  fiats: [],
}

export const setKlines = createAsyncThunk<{marketId: number, klines: IKline}, number, {rejectValue: string}>(
  'config/setKlines',
  async (marketId, { getState }) => {
    const state = getState() as RootState;

    const market = state.public_service_connector.markets.find(m => m.id === marketId);

    const path = state.config.history_feeds.find((obj) => {
      return obj.provider === 'CryptoCompare'
    })!.history_url

    const urlParameters: any = {
      interval: '1h',
      source_currency: market?.source_currency,
      target_currency: market?.target_currency,
      timestamp: Date.now(),
      limit: 24,
      i: 1,
    }
    const query = Object.keys(urlParameters)
      .map((name) => `${name}=${encodeURIComponent(urlParameters[name])}`)
      .join('&')

    const res = await fetch(path + `?${query}`)
    const klines = await res.json()

    return {
      marketId,
      klines
    }
  }
)

export const setConfig = createAsyncThunk('socket/setConfig', async (_, { dispatch }) => {
  const data = await fetchConfig()

  if (data) {
    dispatch(setConfigFrontServiceConnection(data.data.frontServiceWsApi))
    dispatch(setConfigPublicServiceConnection(data?.data?.publicServiceWsApi))
    dispatch(setConfigProxyServiceConnection(data.data.proxyServiceWsApi))
    dispatch(setConfigUserServiceConnection(data.data.userServiceWsApi))
  } else {
    dispatch(createNotificationObject({ message: 'Server Error', id: generateRequestKey('global'), status: 'error' }))
  }
  return data.data
})

export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    updateFromApi: (state, action: PayloadAction<IConfigState>) => {
      state.userServiceWsApi = action.payload.userServiceWsApi
      state.proxyServiceWsApi = action.payload.proxyServiceWsApi
      state.frontServiceWsApi = action.payload.frontServiceWsApi
      state.publicServiceWsApi = action.payload.publicServiceWsApi
      state.coinRateApi = action.payload.coinRateApi
      state.currencies = action.payload.currencies
      state.system_currencies = action.payload.system_currencies
      state.shared_countries = action.payload.shared_countries
      state.history_feeds = action.payload.history_feeds
      state.languages = action.payload.languages
      state.is_freeze_reality_check = action.payload.is_freeze_reality_check
      state.fiats = action.payload.fiats
    },
    addFavoriteMarket: (state, action: PayloadAction<IConfigMarket>) => {
      const market = action.payload

      if (!state.favoriteMarkets.some((m) => m.title === market.title)) {
        state.favoriteMarkets.push(market)
        addFavoriteTitle(market.title)
      }
      state.activeFavoriteMarket = market
    },
    removeFavoriteMarket: (state, action: PayloadAction<string>) => {
      const title = action.payload
      state.favoriteMarkets = state.favoriteMarkets.filter(m => m.title !== title)
      if (title === state.activeFavoriteMarket?.title) {
        state.activeFavoriteMarket = state.favoriteMarkets[0]
      }
      removeFavoriteTitle(title)
    },
    clearFavoriteMarkets: (state) => {
      state.favoriteMarkets = []
      state.activeFavoriteMarket = null
      removeAllFavoriteTitles()
    },
    setActiveFavoriteMarket: (state, action: PayloadAction<IConfigMarket>) => {
      state.activeFavoriteMarket = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(setConfig.fulfilled, (state, action: any) => {
      if (action.payload) {
        state.userServiceWsApi = action.payload.userServiceWsApi
        state.proxyServiceWsApi = action.payload.proxyServiceWsApi
        state.frontServiceWsApi = action.payload.frontServiceWsApi
        state.publicServiceWsApi = action.payload.publicServiceWsApi
        state.coinRateApi = action.payload.coinRateApi
        state.currencies = action.payload.currencies
        state.system_currencies = action.payload.system_currencies
        state.shared_countries = action.payload.shared_countries
        state.favoriteMarkets = syncFavoritesFromLocalStorage(action.payload.markets)
        state.activeFavoriteMarket = state.favoriteMarkets[0]
        state.history_feeds = action.payload.history_feeds
        state.languages = action.payload.languages
        state.isConfigLoading = false
        state.is_freeze_reality_check = action.payload.is_freeze_reality_check
        state.fiats = action.payload.fiats
      }
    })
    builder.addCase(setConfig.pending, (state) => {
      state.isConfigLoading = true
    })
    builder.addCase(setMarketDataAsync.fulfilled, (state, action) => {
      const newMarkets = action.payload.markets.map((m: any) => new ConfigMarket(m))
      const existingIds = new Set(state.markets.map(m => m.id))

      const marketsToAdd = newMarkets.filter(market => !existingIds.has(market.id))

      state.markets = [...state.markets, ...marketsToAdd]
    })
    builder.addCase(setMarketsByIdsDataAsync.fulfilled, (state, action) => {
      const newMarkets = action.payload.markets.map((m: any) => new ConfigMarket(m))
      const existingIds = new Set(state.markets.map(m => m.id))

      const marketsToAdd = newMarkets.filter(market => !existingIds.has(market.id))

      state.markets = [...state.markets, ...marketsToAdd]
    })
    builder.addCase(setKlines.fulfilled, (state, action) => {
      const { marketId, klines } = action.payload;
      const market = state.markets.find(m => m.id === marketId);
      if (market) {
        market.klines = {
          ...klines,
          data: [...klines.data]
        };
      }
    })
  },
})

export const { updateFromApi, addFavoriteMarket, removeFavoriteMarket, clearFavoriteMarkets, setActiveFavoriteMarket } = configSlice.actions

export default configSlice.reducer
